import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { CardHeaderSPMConfig } from './card-header-spm.interface';
import { BehaviorSubject } from 'rxjs';
import { IDetailHeaderSpm } from '@models/spm.model';
import { BaseComponent } from '@shared/base/base.component';
import { StatusSPM } from '../../submenu.enum';
import { ICardHeaderSectionDefault, IChangeCardHeader } from '@shared/components/card/card-header/card-header.model';
import { STRING_CONSTANTS } from '@config/constants/string.constants';
import { UtilitiesService } from '@services/utilities.service';
import { ISPM_ResponseData } from '../../spm.interface';
import { SalesOrderService } from '@pages/sales-order/v1/sales-order.service';
import { ActivatedRoute } from '@angular/router';
import { ProgramMarketingLegacyService } from '@pages/sales-marketing/program-marketing-legacy/program-marketing-legacy.service';

@Component({
  selector: 'app-card-header-spm',
  templateUrl: './card-header-spm.component.html',
  styleUrls: ['./card-header-spm.component.scss'],
})
export class CardHeaderSpmComponent extends BaseComponent implements OnInit, AfterViewInit {
  @Input() title?: string;
  @Input() headerConfig?: CardHeaderSPMConfig;
  @Input() dataSubject!: BehaviorSubject<IDetailHeaderSpm>;
  @Input() isLoading!: BehaviorSubject<boolean>;

  detailChanges!: IChangeCardHeader[];
  dataSection!: ICardHeaderSectionDefault[];

  defaultConfig = <CardHeaderSPMConfig>{
    useStatusBadge: true,
    useSection: false,
  };

  constructor(
    private soService: SalesOrderService,
    private utils: UtilitiesService,
    private activeRoute: ActivatedRoute,
    private programMarketingService: ProgramMarketingLegacyService
  ) {
    super();
  }

  ngOnInit() {
    this.initConfig();
    this.initDetail();
  }

  ngAfterViewInit() {
    if (this.headerConfig?.useSection) this.initHeaderFormCreate();
  }

  initConfig() {
    this.headerConfig = {
      ...this.headerConfig,
      useStatusBadge: this.headerConfig?.useStatusBadge ?? this.defaultConfig.useStatusBadge,
      useSection: this.headerConfig?.useSection ?? this.defaultConfig.useSection,
    };
  }

  initDetail() {
    if (!this.dataSubject) return;
    this.isLoading.next(true);
    this.dataSubject.subscribe((resp) => {
      if (resp && Object.keys(resp).length) {
        this.title = resp.code;
        this.detailChanges = this.detailChangesMapper();
        this.isLoading.next(false);
      }
    });
  }

  initHeaderFormCreate() {
    const warehouse_id = this.activeRoute.snapshot.queryParams['warehouse_id'];
    let initData$ = null;
    if (warehouse_id) {
      initData$ = this.programMarketingService.initHeaderCreateSpm$;
    } else {
      initData$ = this.soService.initHeaderCreateSpm$;
    }
    initData$.pipe(this.takeUntilDestroy()).subscribe((res) => {
      if (!res) return;
      this.dataSectionMapper(res);
    });
  }

  dataSectionMapper(data: ISPM_ResponseData) {
    const { distributor, warehouse, delivery_address } = data;
    this.dataSection = [
      {
        title: 'Distributor',
        value: distributor.name,
        description: distributor.code,
      },
      {
        title: 'Gudang Pengirim',
        value: warehouse.name,
        description: warehouse.address,
      },
      {
        title: 'Alamat Penerima',
        value: delivery_address.label + ' ' + delivery_address.pic_distributor_name,
        description:
          delivery_address.full_address +
          ' ,' +
          delivery_address.sub_district +
          ' ,' +
          delivery_address.district +
          ' ,' +
          delivery_address.regency +
          ' ,' +
          delivery_address.province,
      },
    ];
  }

  detailChangesMapper() {
    const _changes = <IChangeCardHeader[]>[];
    const { is_tag, created_changes, approved_changes, processed_changes, finished_changes } = this.dataSubject.value as IDetailHeaderSpm;
    _changes.push({ title: 'Tanggal Dibuat', ...created_changes }, { title: 'Tanggal Diproses', ...processed_changes }, { title: 'Tanggal Selesai', ...finished_changes });
    if (is_tag) _changes.splice(1, 0, { title: 'Tangal Approve Pusat', ...approved_changes });
    return _changes;
  }

  renderStatusBadge() {
    const { status_enum, status_string } = this.dataSubject.value as IDetailHeaderSpm;
    return `<span class="badge badge__status  badge__status--${status_enum}"> ${status_string} </span>`;
  }

  showCopyClipboard() {
    const _allowedStatus = [StatusSPM.PARTIAL_COMPLETED, StatusSPM.FINISH].toString();

    const _currentStatusEnum = this.dataSubject.value && this.dataSubject.value.status_enum;
    return _allowedStatus.includes(_currentStatusEnum as string);
  }

  getNoteviewText() {
    const { status_enum } = this.dataSubject.value as IDetailHeaderSpm;
    let text = '';
    if (status_enum === StatusSPM.PENDING) text = 'Surat Perintah Muat sedang menunggu persetujuan Admin Pusat.';
    return text;
  }

  getCopyClipboardText(isTagCode = false) {
    const { code_tag, code_order_erp, code } = this.dataSubject.value as IDetailHeaderSpm;
    return isTagCode
      ? { label: 'Tag Code', value: code_tag ?? '' }
      : {
          label: this.generateTitleIdErp(code),
          value: code_order_erp ?? '',
        };
  }

  getCopyClipboardValue = (isTagCode = false) => (isTagCode ? this.dataSubject.value.code_tag : this.dataSubject.value.code_order_erp);

  generateTitleIdErp = (code: string) => (code.substring(0, 3) === 'SPM' ? 'ID SPM ERP:' : 'ID ERP:');

  generateDate(val: number) {
    if (!val) return '-';
    return this.utils.formatEpochToDate(val, 'dd-MM-yyyy, HH:mm:ss');
  }

  hasTagCode = () => !!this.dataSubject.value.code_tag;

  hasErpCode = () => !!this.dataSubject.value.code_order_erp;

  protected readonly StatusSPM = StatusSPM;
  protected readonly STRING_CONSTANTS = STRING_CONSTANTS;
}
